import { ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { photoAssetUrl } from '../lib/photos'

function formatCapturedAt(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

export default function PhotoGallery({ photos = [], initialPhotoId = null, compact = false }) {
  const initialIndex = useMemo(() => {
    const found = photos.findIndex((photo) => String(photo.id) === String(initialPhotoId))
    return found >= 0 ? found : 0
  }, [photos, initialPhotoId])
  const [index, setIndex] = useState(initialIndex)
  const pointerStartRef = useRef(null)

  useEffect(() => setIndex(initialIndex), [initialIndex])
  useEffect(() => {
    if (index > photos.length - 1) setIndex(Math.max(0, photos.length - 1))
  }, [photos.length, index])

  if (!photos.length) return null
  const photo = photos[index]
  const previous = () => setIndex((current) => (current - 1 + photos.length) % photos.length)
  const next = () => setIndex((current) => (current + 1) % photos.length)

  function handlePointerDown(event) {
    pointerStartRef.current = { x: event.clientX, y: event.clientY }
  }

  function handlePointerUp(event) {
    const start = pointerStartRef.current
    pointerStartRef.current = null
    if (!start || photos.length < 2) return
    const deltaX = event.clientX - start.x
    const deltaY = event.clientY - start.y
    if (Math.abs(deltaX) < 45 || Math.abs(deltaX) < Math.abs(deltaY)) return
    if (deltaX < 0) next()
    else previous()
  }

  return (
    <section className={`photo-gallery ${compact ? 'photo-gallery--compact' : ''}`}>
      <div className="photo-gallery__stage" onPointerDown={handlePointerDown} onPointerUp={handlePointerUp}>
        <img src={photoAssetUrl(photo.r2_key)} alt={photo.caption || 'Summit Hub trip photograph'} />
        {photos.length > 1 && (
          <>
            <button type="button" className="photo-gallery__nav photo-gallery__nav--prev" onClick={previous} aria-label="Previous photo"><ChevronLeft size={20} /></button>
            <button type="button" className="photo-gallery__nav photo-gallery__nav--next" onClick={next} aria-label="Next photo"><ChevronRight size={20} /></button>
            <span className="photo-gallery__count">{index + 1} / {photos.length}</span>
          </>
        )}
      </div>
      {(photo.caption || photo.captured_at) && (
        <div className="photo-gallery__meta">
          {photo.caption && <strong>{photo.caption}</strong>}
          {photo.captured_at && <span>{formatCapturedAt(photo.captured_at)}</span>}
        </div>
      )}
      {photos.length > 1 && (
        <div className="photo-gallery__thumbs" aria-label="Photo thumbnails">
          {photos.map((item, itemIndex) => (
            <button
              type="button"
              key={item.id}
              className={itemIndex === index ? 'active' : ''}
              onClick={() => setIndex(itemIndex)}
              aria-label={`View photo ${itemIndex + 1}`}
            >
              {item.thumbnail_r2_key || item.r2_key ? (
                <img src={photoAssetUrl(item.thumbnail_r2_key || item.r2_key)} alt="" />
              ) : <ImageIcon size={17} />}
            </button>
          ))}
        </div>
      )}
    </section>
  )
}
